import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useSidebar } from "../context/useSidebar";
import { canAccessVehicleCatalogRoute } from "@utils/vehicleCatalogAccess";

type NavItem = {
  name: string;
  icon: string;
  path?: string;
  /** Role 2: show item if user.permission includes any of these (default: path without leading slash). */
  permissionPaths?: string[];
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const getNavItems = (t: (key: string) => string): NavItem[] => [
  { icon: "home.png", name: t("sidebar.dashboard"), path: "/dashboard" },
  { icon: "customer.png", name: t("sidebar.customer"), path: "/customer" },
  { icon: "driver.png", name: t("sidebar.driver"), path: "/driver" },
  { icon: "road-trip.png", name: "Booking Management", path: "/booking" },
  { icon: "administrator.png", name: t("sidebar.subAdmin"), path: "/admin" },
  { icon: "wallet.png", name: "Wallet", path: "/wallet" },
  { icon: "transaction.png", name: "Transaction Management", path: "/transaction" },
  { icon: "withdrawal.png", name: "Withdrawal Management", path: "/withdrawal" },
  {
    icon: "transaction.png",
    name: t("sidebar.earningManagement"),
    path: "/earning-management",
  },
  {
    icon: "page.png",
    name: t("sidebar.cmsManagement"),
    path: "/cms-management",
    permissionPaths: [
      "cms-management/menu",
      "cms-management/page",
      "cms-management/faq",
      "cms-management/blogs",
      "cms-management/banner",
      "cms-management/training",
      "cms-management/sms-template",
      "cms-management/email-template",
      "cms-management/notification-template",
      "cms-management/report-issue",
      "cms-management/trip-cancel-manager",
      "cms-management/promo-code",
      "cms-management/state-management",
    ],
  },
  {
    icon: "page.png",
    name: "Banner Management",
    path: "/banner",
    permissionPaths: ["banner", "cms-management/banner"],
  },
  // { icon: "vehicle-type.png", name: t("sidebar.vehicleType"), path: "/vehicle-type" },
  {
    icon: "vehicle-catalog.png",
    name: t("sidebar.vehicleCatalog"),
    path: "/vehicle-catalog",
    permissionPaths: ["vehicle-catalog", "fuel-type", "ride-type", "vehicle-category"],
  },
  { icon: "vehicle-type.png", name: "Vehicle Management", path: "/vehicle" },
  { icon: "notification.png", name: "Notification", path: "/notification" },
  {
    icon: "page.png",
    name: "Contact enquiries",
    path: "/contact-enquiry",
    permissionPaths: ["contact-enquiry", "cms-management/contact-enquiry"],
  },
  { icon: "location.png", name: "Geo-Fencing Management", path: "/geo-fencing" },
  { icon: "review.png", name: "Review List", path: "/review" },
  { icon: "report.png", name: "Driver Report Management", path: "/driver-report" },
  { icon: "setting.png", name: t("sidebar.settings"), path: "/setting" },
];

const AppSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { isExpanded, isMobileOpen, isHovered } = useSidebar();
  const location = useLocation();
  const pathArray = location.pathname.split("/").filter(Boolean);
  const pathString = `/${pathArray[0]}`;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActive = useCallback(
    (path: string) => {
      if (path === "/geo-fencing") {
        return (
          location.pathname === "/geo-fencing" ||
          location.pathname.startsWith("/geo-fencing/")
        );
      }
      if (path === "/vehicle-catalog") {
        return location.pathname === "/vehicle-catalog";
      }
      if (path === "/earning-management") {
        return location.pathname === "/earning-management";
      }
      if (path === "/cms-management") {
        return (
          location.pathname === "/cms-management" ||
          location.pathname.startsWith("/cms-management/")
        );
      }
      if (path === "/banner") {
        return location.pathname === "/banner";
      }
      return location.pathname === path || pathString === path;
    },
    [location.pathname, pathString]
  );
  const isCollapsed = !isExpanded && !isHovered && !isMobileOpen;
  const navItems = getNavItems(t);

  const collapsedNavLinkClass = isCollapsed
    ? "justify-center gap-0 px-1.5 py-2.5 max-w-[52px] mx-auto w-full min-w-0"
    : "";

  const renderMenuItems = (items: NavItem[]) => (
    <ul className={`flex flex-col gap-4 ${isCollapsed ? "items-center" : ""}`}>
      {items.map((nav) => {
        if (!nav.path) return null;

        const linkElement = (
          <Link
            to={nav.path}
            className={`menu-item group ${collapsedNavLinkClass} ${
              isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
            }`}
          >
            <span
              className={`menu-item-icon-size ${
                isActive(nav.path)
                  ? "menu-item-icon-active"
                  : "menu-item-icon-inactive"
              }`}
            >
              <img
                src={`/images/icons/${
                  isActive(nav.path) ? "active-" : "inactive-"
                }${nav.icon}`}
                alt={nav.name}
              />
            </span>

            {(isExpanded || isHovered || isMobileOpen) && (
              <span
                className={`menu-item-text ${
                  isActive(nav.path) ? "text-black" : "text-white"
                }`}
              >
                {nav.name}
              </span>
            )}
          </Link>
        );

        return (
          <li
            key={nav.name}
            className={isCollapsed ? "flex w-full min-w-0 justify-center" : ""}
          >
            {isCollapsed ? (
              <Tooltip placement="right" title={nav.name} color="#0056D2">
                <span className="flex w-full min-w-0 max-w-full justify-center">
                  {linkElement}
                </span>
              </Tooltip>
            ) : (
              linkElement
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderAdminMenuItems = (items: NavItem[]) => (
    <ul className={`flex flex-col gap-4 ${isCollapsed ? "items-center" : ""}`}>
      {items.map((nav) => {
        const cleanPath = nav.path?.replace(/^\//, "");
        const permKeys = nav.permissionPaths?.length
          ? nav.permissionPaths
          : cleanPath
            ? [String(cleanPath)]
            : [];
        const hasPermission =
          nav.path === "/" ||
          permKeys.some((p) => user?.permission?.includes(p));

        if (!nav.path || !hasPermission) return null;

        const linkElement = (
          <Link
            to={nav.path}
            className={`menu-item group ${collapsedNavLinkClass} ${
              isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
            }`}
          >
            <span
              className={`menu-item-icon-size ${
                isActive(nav.path)
                  ? "menu-item-icon-active"
                  : "menu-item-icon-inactive"
              }`}
            >
              <img
                src={`/images/icons/${
                  isActive(nav.path) ? "active-" : "inactive-"
                }${nav.icon}`}
                alt={nav.name}
              />
            </span>

            {(isExpanded || isHovered || isMobileOpen) && (
              <span
                className={`menu-item-text ${
                  isActive(nav.path) ? "text-black" : "text-white"
                }`}
              >
                {nav.name}
              </span>
            )}
          </Link>
        );

        return (
          <li
            key={nav.name}
            className={isCollapsed ? "flex w-full min-w-0 justify-center" : ""}
          >
            {isCollapsed ? (
              <Tooltip placement="right" title={nav.name} color="#0056D2">
                <span className="flex w-full min-w-0 max-w-full justify-center">
                  {linkElement}
                </span>
              </Tooltip>
            ) : (
              linkElement
            )}
          </li>
        );
      })}
    </ul>
  );
  useEffect(() => {
    if (user?.role_id !== 2) return;

    const perms = user.permission;
    // Avoid redirect while user/permissions are still loading (persist/API) — that caused
    // navigating to a valid menu route, then a few seconds later jumping back to home.
    if (!Array.isArray(perms)) {
      return;
    }

    const path = location.pathname;
    const cleanPath = path.replace(/^\//, "");

    const alwaysAllowed =
      path === "/" || path === "/dashboard" || path === "/profile";

    let hasPermission =
      alwaysAllowed ||
      perms.some(
        (perm) =>
          cleanPath === perm || cleanPath.startsWith(`${perm}/`),
      );

    if (cleanPath === "contact-enquiry") {
      hasPermission =
        alwaysAllowed ||
        perms.includes("contact-enquiry") ||
        perms.includes("cms-management/contact-enquiry");
    }

    if (cleanPath === "banner") {
      hasPermission =
        alwaysAllowed ||
        perms.includes("banner") ||
        perms.includes("cms-management/banner");
    }

    if (cleanPath === "cms-management") {
      const cmsHubPerms = [
        "cms-management/menu",
        "cms-management/page",
        "cms-management/faq",
        "cms-management/blogs",
        "cms-management/banner",
        "cms-management/training",
        "cms-management/sms-template",
        "cms-management/email-template",
        "cms-management/notification-template",
        "cms-management/report-issue",
        "cms-management/trip-cancel-manager",
        "cms-management/promo-code",
        "cms-management/state-management",
      ];
      hasPermission =
        alwaysAllowed || perms.some((p) => cmsHubPerms.includes(p));
    }

    if (path === "/vehicle-catalog" || path.startsWith("/vehicle-catalog/")) {
      hasPermission =
        alwaysAllowed || canAccessVehicleCatalogRoute(perms);
    }

    if (!hasPermission) {
      navigate("/dashboard", { replace: true });
    }
  }, [location.pathname, navigate, user]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <aside
      className={`fixed mt-16 flex flex-col overflow-x-hidden lg:mt-0 top-0 left-0 bg-[#0056D2] dark:bg-gray-900 dark:border-gray-800 
      text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200
        ${
          isExpanded || isMobileOpen
            ? "w-[290px] px-5"
            : isHovered
            ? "w-[290px] px-5"
            : "w-[90px] px-2"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
    >
      <div
        className={`flex items-center shrink-0 ${
          !isExpanded && !isHovered && !isMobileOpen
            ? "justify-center px-3 pt-1 pb-5"
            : "justify-start px-4 pt-1 pb-5"
        }`}
      >
        <Link
          to="/"
          className={`flex items-center justify-center ${
            !isExpanded && !isHovered && !isMobileOpen ? "flex-1 min-w-0" : ""
          }`}
        >
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <img
                className="object-contain dark:hidden"
                style={{
                  filter: "brightness(0) invert(1)",
                  height: "64px",
                  minHeight: "64px",
                  width: "auto",
                  maxWidth: "100%",
                }}
                src="/images/logo/logo.svg"
                alt="TovRide"
              />
              <img
                className="hidden object-contain dark:block"
                style={{
                  filter: "brightness(0) invert(1)",
                  height: "64px",
                  minHeight: "64px",
                  width: "auto",
                  maxWidth: "100%",
                }}
                src="/images/logo/logo-dark.svg"
                alt="TovRide"
              />
            </>
          ) : (
            <img
              src="/images/logo/auth-logo.svg"
              alt="TovRide"
              className="object-contain"
              style={{
                width: "72px",
                height: "72px",
                minWidth: "72px",
                minHeight: "72px",
              }}
            />
          )}
        </Link>
      </div>

      <div
        ref={scrollContainerRef}
        className={`flex flex-col overflow-y-auto duration-300 ease-linear sidebar-scrollbar ${
          isScrolling ? "scrolling" : ""
        }`}
      >
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            {user?.role_id === 1 && <div>{renderMenuItems(navItems)}</div>}
            {user?.role_id === 2 && <div>{renderAdminMenuItems(navItems)}</div>}
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
